import './OrderDetailPage.scss';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Phone, Send, MessageCircle } from 'lucide-react';
import { useLang } from '../../i18n';
import { get } from '../../services/api';
import Card from '../../components/Card';
import Tabs from '../../components/Tabs';
import OrderStatusBadge from '../../components/OrderStatusBadge';
import OrderProgressBar from '../../components/OrderProgressBar';
import Skeleton from '../../components/Skeleton';
import type { Order } from '../../types';
import OrderOverview from './components/OrderOverview';
import OrderStages from './components/OrderStages';
import OrderFiles from './components/OrderFiles';
import OrderChat from './components/OrderChat';
import OrderInvoices from './components/OrderInvoices';

type TabKey = 'overview' | 'stages' | 'files' | 'chat' | 'invoices';

const TELEGRAM_USER = 'mrbek112';
const WHATSAPP_PHONE = '+998900770302';
const PHONE = '+998 90 077 03 02';

function progress(o: Order): number {
  if (!o.stages || o.stages.length === 0) return 0;
  // Если у этапов проставлены веса (weight_pct) — считаем взвешенный прогресс.
  const totalW = o.stages.reduce((sum, s: any) => sum + (Number(s.weight_pct) || 0), 0);
  if (totalW > 0) {
    const doneW = o.stages
      .filter((s) => s.status === 'done')
      .reduce((sum, s: any) => sum + (Number(s.weight_pct) || 0), 0);
    return Math.min(100, Math.round((doneW / totalW) * 100));
  }
  // Иначе — по доле выполненных.
  const done = o.stages.filter((s) => s.status === 'done').length;
  return Math.round((done / o.stages.length) * 100);
}

export default function OrderDetailPage() {
  const { public_id } = useParams<{ public_id: string }>();
  const { t } = useLang();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<TabKey>('overview');

  useEffect(() => {
    if (!public_id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await get<Order>(`/me/orders/${public_id}/`);
        if (!cancelled) setOrder(res);
      } catch {
        if (!cancelled) setOrder(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [public_id]);

  const tabs = useMemo(() => ([
    { value: 'overview', label: t('order.overview') },
    { value: 'stages', label: t('order.stages') },
    { value: 'files', label: t('order.files') },
    { value: 'chat', label: t('order.chat') },
    { value: 'invoices', label: t('order.invoices') },
  ]), [t]);

  if (loading) return <Skeleton height={200} />;
  if (!order || !public_id) return <Card padding="lg">{t('common.error')}</Card>;

  return (
    <div className="order-detail">
      <Card padding="lg" className="order-detail__hero">
        <div className="order-detail__hero-head">
          <div className="order-detail__hero-info">
            <div className="order-detail__hero-id">{order.human_id}</div>
            <h1 className="order-detail__hero-title">{order.title}</h1>
            <OrderStatusBadge status={order.status} />
          </div>
          <div className="order-detail__contacts" aria-label={t('cabinet.manager')}>
            <a className="order-detail__contact" href={`https://t.me/${TELEGRAM_USER}`} target="_blank" rel="noreferrer noopener" aria-label="Telegram">
              <Send size={18} />
            </a>
            <a className="order-detail__contact" href={`https://wa.me/${WHATSAPP_PHONE.replace(/\D/g, '')}`} target="_blank" rel="noreferrer noopener" aria-label="WhatsApp">
              <MessageCircle size={18} />
            </a>
            <a className="order-detail__contact" href={`tel:${PHONE.replace(/\s/g, '')}`} aria-label={t('contact.phone')}>
              <Phone size={18} />
            </a>
          </div>
        </div>
        <hr className="order-detail__divider" />
        <OrderProgressBar value={progress(order)} max={100} showValue />
      </Card>

      <Tabs value={tab} onChange={(v) => setTab(v as TabKey)} items={tabs} ariaLabel={t('order.overview')} />

      <div className="order-detail__panel">
        {tab === 'overview' && <OrderOverview order={order} />}
        {tab === 'stages' && <OrderStages stages={order.stages || []} />}
        {tab === 'files' && <OrderFiles publicId={public_id} />}
        {tab === 'chat' && <OrderChat publicId={public_id} />}
        {tab === 'invoices' && <OrderInvoices publicId={public_id} />}
      </div>
    </div>
  );
}
